// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getHeader } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import {
    getPageLinkByNodeId,
    getPageLinkByPageType,
    useGetLink,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ActiveState, Jsonld, SeoProps } from "@stoneage/App/Widgets/SearchEngineOptimization/SearchEngineOptimization";

/**
 * Activate structure based on condition
 *
 * @param SeoProps
 * @returns ActiveState
 */
const activate = ({ state, fields, payload }: SeoProps): ActiveState => {
    const allProductLink = useGetLink(
        getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
            return widget.type === "Header/MainNavigation";
        })?.fields?.productsLink,
    );
    const brandsPage = getPageLinkByPageType(state, "BrandsPage");
    payload.allProductLink = { url: allProductLink?.url || "/", title: allProductLink?.title || "Products" };
    payload.brandsPage = { url: brandsPage?.url || "/", title: brandsPage?.title || "Brands" };
    return true;
};

/**
 * Build the structured data
 *
 * @param SeoProps
 * @returns Jsonld
 */
const json = ({ state, url, payload }: SeoProps): Jsonld => {
    const mainNavigationLinks =
        getWidgetsByPageId(state, getHeader(state).id)
            .find((widget: WidgetProps) => {
                return widget.type === "Header/MainNavigation";
            })
            ?.fields?.links.map((link: any) => {
                const {
                    fields: { destination, overrideTitle, title },
                } = link;
                const { type, value } = destination;
                let mappedLink: any;

                if (type === "Url") {
                    mappedLink = {
                        url: value,
                        title,
                    };
                } else if (type === "Page") {
                    const readonlyPageLink = getPageLinkByNodeId(state, value);

                    if (readonlyPageLink) {
                        mappedLink = { url: readonlyPageLink.url, title: readonlyPageLink.title };
                        if (overrideTitle) {
                            mappedLink.title = overrideTitle;
                        }
                    }
                } else if (type === "Category") {
                    mappedLink = {
                        url: payload.allProductLink?.url,
                        title: payload.allProductLink?.title || translate("Products"),
                    };
                }

                return mappedLink;
            })
            .filter((i: any) => i && i?.url !== payload.brandsPage?.url && i?.url !== payload.allProductLink?.url) ||
        [];

    const anotherLinks =
        getWidgetsByPageId(state, getHeader(state).id)
            .find((widget: WidgetProps) => {
                return widget.type === "Header/HeaderLinkList";
            })
            ?.fields?.links?.map((link: any) => {
                const {
                    fields: { destination, overrideTitle, title },
                } = link;
                const { type, value } = destination;
                let mappedLink: any;

                if (type === "Url") {
                    mappedLink = {
                        url: value,
                        title,
                        type,
                    };
                } else if (type === "Page") {
                    const readonlyPageLink = getPageLinkByNodeId(state, value);

                    if (readonlyPageLink && readonlyPageLink.type !== "HomePage") {
                        mappedLink = { url: readonlyPageLink.url, title: readonlyPageLink.title };
                        if (overrideTitle) {
                            mappedLink.title = overrideTitle;
                        }
                    }
                }

                return mappedLink;
            })
            .filter((i: any) => i && i?.url !== payload.brandsPage?.url && i?.url !== payload.allProductLink?.url) ||
        [];

    const links = [payload.allProductLink, ...mainNavigationLinks, ...anotherLinks];

    return {
        "@context": "https://schema.org",
        "@graph": links?.map((item: any, index: number) => ({
            "@context": "https://schema.org",
            "@type": "SiteNavigationElement",
            name: item.title,
            url: item.type === "Url" ? item.url : url + item.url,
            position: index + 1,
        })),
    };
};

export default { activate, json };
